import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { getServices } from "../lib/servicesCache";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Search, Sparkles, X } from "lucide-react";
import { CategoryChipBar } from "../components/CategoryChipBar";
import { BookingServiceRow, BookingPackageRow } from "../components/BookingServiceRow";
import { BookingCartBar } from "../components/BookingCartBar";
import { sortCategoriesByOrder } from "../lib/categoryOrder";
import { resolveCategoryParam } from "../components/ServiceBubbles";
import BookingProgress from "../components/BookingProgress";

const DRAFT_KEY = "tresses_booking_draft";

const readDraft = () => {
    try {
        const raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const d = JSON.parse(raw);
        return (d && typeof d === "object") ? d : null;
    } catch { return null; }
};

export default function BookingPage() {
    const { user } = useAuth();
    const { settings } = useSettings();
    const minAmount = settings.min_booking_amount ?? 80;
    const draft = readDraft() || {};
    const [services, setServices] = useState([]);
    const [packages, setPackages] = useState([]);
    const [selectedIds, setSelectedIds] = useState(Array.isArray(draft.selectedIds) ? draft.selectedIds : []);
    const [selectedPackageIds, setSelectedPackageIds] = useState(Array.isArray(draft.selectedPackageIds) ? draft.selectedPackageIds : []);
    const [variantSelections, setVariantSelections] = useState(
        (draft.variantSelections && typeof draft.variantSelections === "object") ? draft.variantSelections : {}
    );
    const [addonSelections, setAddonSelections] = useState(
        (draft.addonSelections && typeof draft.addonSelections === "object") ? draft.addonSelections : {}
    );
    const [packagePromo, setPackagePromo] = useState(null);
    const [activeCategory, setActiveCategory] = useState("All");
    const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("pkg") ? "packages" : "services"));
    const [query, setQuery] = useState("");
    const [openVariantFor, setOpenVariantFor] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const navigate = useNavigate();
    const [params] = useSearchParams();

    useEffect(() => {
        getServices().then((data) => {
            setServices(data);
            const cat = resolveCategoryParam(params.get("category"), [...new Set(data.map((x) => x.category).filter(Boolean))]);
            if (cat) setActiveCategory(cat);
            const sid = params.get("service");
            if (sid && data.find((x) => x.id === sid)) {
                setSelectedIds((prev) => prev.includes(sid) ? prev : [...prev, sid]);
                const vid = params.get("variant");
                if (vid && (data.find((x) => x.id === sid).variants || []).some((v) => v.id === vid)) {
                    setVariantSelections((prev) => ({ ...prev, [sid]: vid }));
                }
            }
        });
        api.get("/packages", { params: { active_only: true } }).then((r) => {
            setPackages(r.data || []);
            const pid = params.get("pkg");
            if (pid && (r.data || []).find((p) => p.id === pid)) {
                setSelectedPackageIds((prev) => prev.includes(pid) ? prev : [...prev, pid]);
            }
        }).catch(() => {});
        const promoId = params.get("promo");
        const fromId = params.get("from");
        const couponParam = params.get("coupon");
        // Promo attribution: package promo OR coupon-style promo arrival
        const attributionId = promoId || fromId;
        if (attributionId || couponParam) {
            try {
                const cur = readDraft() || {};
                const next = { ...cur };
                if (attributionId) next.attribution_promotion_id = attributionId;
                if (couponParam) next.coupon = couponParam.trim().toUpperCase();
                sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
                window.dispatchEvent(new Event("tresses-draft-changed"));
                if (couponParam) toast.success(`Coupon ${couponParam.toUpperCase()} ready to apply at checkout`);
            } catch { /* ignore */ }
        }
        if (promoId) {
            api.get("/promotions").then((r) => {
                const p = r.data.find((x) => x.id === promoId && x.active && x.type === "package");
                if (p) {
                    setPackagePromo(p);
                    setSelectedIds(p.service_ids || []);
                    toast.success(`${p.title} — package applied`);
                } else {
                    toast.error("This package is no longer available");
                }
            });
        }
    }, [params]);

    // Persist draft so the back button retains selections
    useEffect(() => {
        try {
            const cur = readDraft() || {};
            sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
                ...cur,
                selectedIds,
                selectedPackageIds,
                variantSelections,
                addonSelections,
                promotion_id: packagePromo?.id || null,
                owner_email: user?.email ? user.email.toLowerCase() : (cur.owner_email || null),
            }));
            window.dispatchEvent(new Event("tresses-draft-changed"));
        } catch { /* ignore quota errors */ }
    }, [selectedIds, selectedPackageIds, variantSelections, addonSelections, packagePromo, user?.email]);

    const toggleService = (s) => {
        if (packagePromo) { toast.error("Package services are fixed"); return; }
        const isSel = selectedIds.includes(s.id);
        if (!isSel && (s.variants || []).length > 0 && !variantSelections[s.id]) {
            setOpenVariantFor((cur) => (cur === s.id ? null : s.id));
            return;
        }
        if (isSel) removeService(s.id);
        else setSelectedIds((prev) => [...prev, s.id]);
    };

    const removeService = (id) => {
        setSelectedIds((prev) => prev.filter((x) => x !== id));
        setVariantSelections(({ [id]: _v, ...rest }) => rest);
        setAddonSelections(({ [id]: _a, ...rest }) => rest);
        setOpenVariantFor(null);
    };

    const pickVariant = (s, vid) => {
        setVariantSelections((prev) => ({ ...prev, [s.id]: vid }));
        setSelectedIds((prev) => (prev.includes(s.id) ? prev : [...prev, s.id]));
        setOpenVariantFor(null);
    };

    const togglePackage = (id) => {
        if (packagePromo) { toast.error("Package services are fixed"); return; }
        setSelectedPackageIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const selectedPackages = useMemo(
        () => selectedPackageIds.map((id) => packages.find((p) => p.id === id)).filter(Boolean),
        [selectedPackageIds, packages]
    );

    const clearPackage = () => {
        setPackagePromo(null);
        setSelectedIds([]);
        navigate("/book", { replace: true });
    };

    const selectedServices = useMemo(
        () => selectedIds.map((id) => services.find((s) => s.id === id)).filter(Boolean),
        [selectedIds, services]
    );

    const priceOf = (s) => {
        const v = (s.variants || []).find((x) => x.id === variantSelections[s.id]);
        const base = v ? Number(v.price) : Number(s.price);
        const chosen = addonSelections[s.id] || [];
        const addonSum = (s.addons || [])
            .filter((a) => chosen.includes(a.id))
            .reduce((sum, a) => sum + Number(a.price || 0), 0);
        return base + addonSum;
    };
    const toggleAddon = (sid, aid) => {
        setAddonSelections((prev) => {
            const cur = prev[sid] || [];
            const next = cur.includes(aid) ? cur.filter((x) => x !== aid) : [...cur, aid];
            return { ...prev, [sid]: next };
        });
    };
    const displayNameOf = (s) => {
        const v = (s.variants || []).find((x) => x.id === variantSelections[s.id]);
        return v ? `${s.name} \u2014 ${v.name}` : s.name;
    };
    const servicesMissingVariant = selectedServices.filter((s) => (s.variants || []).length > 0 && !variantSelections[s.id]);
    const packagesSubtotal = selectedPackages.reduce((sum, p) => sum + Number(p.price || 0), 0);
    const subtotal = packagePromo ? Number(packagePromo.package_price || 0) : selectedServices.reduce((sum, s) => sum + priceOf(s), 0) + packagesSubtotal;

    const continueToCheckout = () => {
        if (selectedIds.length === 0 && selectedPackageIds.length === 0) {
            toast.error("Choose at least one service or package");
            return;
        }
        if (servicesMissingVariant.length > 0) {
            toast.error(`Pick an option for: ${servicesMissingVariant.map((s) => s.name).join(", ")}`);
            return;
        }
        if (!packagePromo && subtotal < minAmount) {
            toast.error(`Add S$${(minAmount - subtotal).toFixed(2)} more to meet the minimum.`);
            return;
        }
        if (!user) {
            toast("Sign in to pick your date & time — your selection is saved.");
            navigate("/login?next=/checkout");
            return;
        }
        navigate("/checkout");
    };

    const cartItems = [
        ...selectedServices.map((s) => ({ key: s.id, kind: "service", name: displayNameOf(s), price: priceOf(s) })),
        ...selectedPackages.map((p) => ({ key: p.id, kind: "package", name: p.name, price: Number(p.price || 0) })),
    ];
    const removeCartItem = (it) => {
        if (it.kind === "package") togglePackage(it.key);
        else removeService(it.key);
    };
    useEffect(() => { if (cartItems.length === 0) setCartOpen(false); }, [cartItems.length]);
    const clearCart = () => { setSelectedIds([]); setSelectedPackageIds([]); setVariantSelections({}); setAddonSelections({}); setCartOpen(false); };

    const grouped = useMemo(() => {
        const acc = {};
        services.forEach((s) => { (acc[s.category] = acc[s.category] || []).push(s); });
        return sortCategoriesByOrder(Object.entries(acc), settings.category_order);
    }, [services, settings.category_order]);

    const settingsLoaded = "booking_packages_enabled" in settings;
    const packagesEnabled = settings.booking_packages_enabled === true && packages.length > 0;
    // Drop packages from the cart when admin has disabled package booking
    useEffect(() => {
        if (settingsLoaded && settings.booking_packages_enabled !== true && selectedPackageIds.length) setSelectedPackageIds([]);
    }, [settingsLoaded, settings.booking_packages_enabled, selectedPackageIds.length]);
    const showPackages = packagesEnabled && mode === "packages";

    const q = query.trim().toLowerCase();
    const visibleGroups = q
        ? grouped.map(([cat, items]) => [cat, items.filter((s) => s.name.toLowerCase().includes(q) || (cat || "").toLowerCase().includes(q))]).filter(([, items]) => items.length)
        : activeCategory === "All" ? grouped : grouped.filter(([cat]) => cat === activeCategory);
    const visiblePackages = showPackages ? (q ? packages.filter((p) => p.name.toLowerCase().includes(q)) : packages) : [];

    const chips = [{ value: "All", label: "All", count: services.length }];
    grouped.forEach(([cat, items]) => chips.push({ value: cat, label: cat, count: items.length }));

    const renderRow = (s) => (
        <BookingServiceRow
            key={s.id}
            s={s}
            selected={selectedIds.includes(s.id)}
            variantId={variantSelections[s.id]}
            addonIds={addonSelections[s.id] || []}
            open={openVariantFor === s.id}
            onToggle={() => toggleService(s)}
            onPickVariant={(vid) => pickVariant(s, vid)}
            onToggleAddon={(aid) => toggleAddon(s.id, aid)}
        />
    );

    const SectionTitle = ({ children, count }) => (
        <h2 className="flex items-baseline justify-between pt-8 pb-2 border-b border-[#C29A5B]/50">
            <span className="font-serif-display text-2xl text-[#6B1535]">{children}</span>
            {count != null && <span className="text-xs text-[#8A6574]">{count}</span>}
        </h2>
    );

    return (
        <div className="min-h-screen pt-28 pb-40" data-testid="booking-page">
            <div className="max-w-3xl mx-auto px-5 sm:px-6">
                <BookingProgress step={1} />
                <div className="flex items-end justify-between gap-4 mt-5 flex-wrap">
                    <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F]">Book your services</h1>
                    <Link to="/book/group" className="text-xs text-[#6B1535] underline underline-offset-4 hover:text-[#8F622C]" data-testid="link-group-booking">Booking for a group?</Link>
                </div>
                <p className="text-sm text-[#7A5262] mt-2" data-testid="min-amount-notice">Tap ＋ to add. Minimum booking S${minAmount}.</p>

                {packagePromo ? (
                    <section className="mt-8" data-testid="package-banner">
                        <div className="flex items-start justify-between gap-3 p-4 border border-[#C29A5B]/60 bg-white">
                            <div>
                                <div className="text-overline flex items-center gap-2"><Sparkles size={12} /> Package applied</div>
                                <div className="font-serif-display text-xl text-[#3D0C1F] mt-1">{packagePromo.title}</div>
                            </div>
                            <button type="button" onClick={clearPackage} className="text-xs text-[#7A5262] hover:text-red-700 inline-flex items-center gap-1" data-testid="clear-package"><X size={13} /> Remove</button>
                        </div>
                        <div className="mt-2" data-testid="selected-services-panel">
                            {selectedServices.map((s) => (
                                <div key={s.id} className="flex justify-between py-3 border-b border-[#6B1535]/10 text-[#3D0C1F]" data-testid={`selected-${s.id}`}>
                                    <span>{s.name}</span><span className="text-[#8F622C] text-sm">Included</span>
                                </div>
                            ))}
                        </div>
                    </section>
                ) : (
                    <>
                        <div className="sticky top-[78px] md:top-[86px] z-20 -mx-5 sm:-mx-6 px-5 sm:px-6 pt-4 pb-2 mt-4 bg-[#FBF6F0]/95 backdrop-blur-md" data-testid="booking-toolbar">
                            {packagesEnabled && (
                                <div className="grid grid-cols-2 p-1 mb-3 rounded-full bg-white border border-[#C29A5B]/50" role="tablist" data-testid="booking-mode-switch">
                                    {[["services", "Services"], ["packages", "Packages"]].map(([v, label]) => (
                                        <button key={v} type="button" role="tab" aria-selected={mode === v} onClick={() => { setMode(v); setQuery(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                            className={`py-2 rounded-full text-xs tracking-[0.2em] uppercase transition-colors ${mode === v ? "bg-[#6B1535] text-[#FFF8F2]" : "text-[#6B1535] hover:bg-[#6B1535]/5"}`}
                                            data-testid={`booking-mode-${v}`}>
                                            {label}{v === "packages" && selectedPackageIds.length ? ` · ${selectedPackageIds.length}` : ""}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <label className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#C29A5B]/50 rounded-full focus-within:border-[#6B1535]">
                                <Search size={16} className="text-[#8F622C]" />
                                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={showPackages ? "Search packages" : "Search services"} className="flex-1 bg-transparent outline-none text-sm text-[#3D0C1F] placeholder:text-[#8A6574]" data-testid="booking-search" />
                                {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" data-testid="booking-search-clear"><X size={14} className="text-[#7A5262]" /></button>}
                            </label>
                            {!q && !showPackages && (
                                <div className="mt-3">
                                    <CategoryChipBar chips={chips} active={activeCategory} onSelect={(v) => { setActiveCategory(v); window.scrollTo({ top: 0, behavior: "smooth" }); }} testIdPrefix="booking-chip" barTestId="booking-category-chip-bar" sticky={false} />
                                </div>
                            )}
                        </div>

                        <div data-testid="services-grid">
                            {visiblePackages.length > 0 && (
                                <section data-testid="packages-grid">
                                    <SectionTitle count={visiblePackages.length}>Packages</SectionTitle>
                                    {visiblePackages.map((p) => <BookingPackageRow key={p.id} p={p} selected={selectedPackageIds.includes(p.id)} onToggle={() => togglePackage(p.id)} />)}
                                </section>
                            )}
                            {!showPackages && visibleGroups.map(([cat, items]) => (
                                <section key={cat} data-testid={`booking-category-${cat}`}>
                                    <SectionTitle count={items.length}>{cat}</SectionTitle>
                                    {items.map(renderRow)}
                                </section>
                            ))}
                            {((!showPackages && services.length > 0 && visibleGroups.length === 0) || (showPackages && visiblePackages.length === 0)) && (
                                <p className="text-sm text-[#7A5262] py-10 text-center" data-testid="booking-no-results">No {showPackages ? "packages" : "services"} match “{query}”.</p>
                            )}
                        </div>
                    </>
                )}
            </div>

            <BookingCartBar
                items={cartItems}
                subtotal={subtotal}
                minAmount={packagePromo ? 0 : minAmount}
                missing={servicesMissingVariant.map((s) => s.name)}
                locked={!!packagePromo}
                open={cartOpen}
                setOpen={setCartOpen}
                onContinue={continueToCheckout}
                onRemove={removeCartItem}
                onClear={clearCart}
            />
        </div>
    );
}
